import JumboCardQuick from '@jumbo/components/JumboCardQuick';
import { Autocomplete, Box, Button, Card, FormControl, FormControlLabel, FormLabel, Grid, Radio, RadioGroup, TextField } from "@mui/material";
import { getObjectListByPropertyApi, postSaveObjectApi, putUpdateObjectApi } from "api/general";
import { calculateNewDecisionNo, stripHTMLTags, updateSortedAgendaList } from 'app/utils/appHelpers';
import { DECISION, DECISION_FILE } from "app/utils/constants/customConstant";
import config from 'config';
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setAllMeetingList, setAllMMList, setCurrentAgenda, showAgendaSnackbar } from 'stores/meeting';
import Swal from "sweetalert2";
import AgendaFileDialog from '../Dialog/AgendaFileDialog';
import AgendaTemplateSelectionDialog from '../Dialog/AgendaTemplateSelectionDialog';

const isExistingAgenda = (agendaItem) =>
    Boolean(agendaItem && !Array.isArray(agendaItem) && agendaItem.id);

const getDecisionNoRange = (agendaItem, agendaList) => {
    let filteredAgendas;
    if (agendaItem.supp === 'Evet') {
        filteredAgendas = agendaList.filter((a) => a.supp === 'Evet');
    } else {
        filteredAgendas = agendaList.filter(
            (a) =>
                a.subjectId === agendaItem.subjectId &&
                a.department === agendaItem.department
        );
    }
    if (!filteredAgendas.length) {
        return { allowedMin: 0, allowedMax: 0 };
    }
    return {
        allowedMin: Math.min(...filteredAgendas.map((a) => a.decisionNo)),
        allowedMax: Math.max(...filteredAgendas.map((a) => a.decisionNo)),
    };
};

function AddUpdateAgendaCard({ onClose }) {
    const dispatch = useDispatch();
    const authUser = useSelector(state => state.auth.authUser);
    const currentMeeting = useSelector((state) => state.meeting.currentMeeting);
    const currentAgenda = useSelector((state) => state.meeting.currentAgenda);
    const subjectList = useSelector((state) => state.subject.data);

    const currentAgendaList = useSelector((state) => state.meeting.currentAgendaList);
    const departmentList = config.departments;
    const [isDecisionNoDisabled, setIsDecisionNoDisabled] = useState(false);

    const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
    const [templateType, setTemplateType] = useState('agenda');
    const [fileDialogStatus, setFileDialogStatus] = useState(false);
    const [agendaFileList, setAgendaFileList] = useState([]);
    const { data: userList } = useSelector(state => state.user);

    let allMeetingList = useSelector((state) => state.meeting.allMeetingList);
    let allMMList = useSelector((state) => state.meeting.allMMList);

    const emptyAgenda = {
        adminUserId: authUser.id,
        meetingId: currentMeeting.id,
        principle: "Hayır",
        supp: "Hayır",
        department: '',
        secret: "Hayır",
        status: 0,
        yaysisMeetingId: 0,
        yaysisBoardId: 0,
        yaysisPubId: 0,
        saysMeetingId: 0,
        saysItemId: 0,
        pdate: new Date()
    };

    const [agenda, setAgenda] = useState(() =>
        isExistingAgenda(currentAgenda) ? currentAgenda : emptyAgenda
    );

    const isUpdateMode = isExistingAgenda(agenda);
    const type = isUpdateMode ? 'update' : 'add';

    const [agendaError, setAgendaError] = useState({
        subjectId: "",
        decisionNo: "",
        agenda: ""
    });

    useEffect(() => {
        if (type === "add" && (!agenda.department || agenda.department.trim() === "") && currentMeeting?.inst) {
            setAgenda(prev => ({ ...prev, department: currentMeeting.inst }));
        }
    }, [type, currentMeeting, agenda.department]);

    const handleTemplateSelect = (templateContent) => {
        if (templateType === 'agenda') {
            setAgenda(prev => {
                const currentAgenda = stripHTMLTags(prev.agenda || '');
                const newAgenda = currentAgenda.trim() !== ''
                    ? currentAgenda + '\n\n' + templateContent
                    : templateContent;
                return { ...prev, agenda: newAgenda };
            });
            setAgendaError(prev => ({ ...prev, agenda: "" }));
        } else {
            setAgenda(prev => {
                const currentDecision = stripHTMLTags(prev.decision || '');
                const newDecision = currentDecision.trim() !== ''
                    ? currentDecision + '\n\n' + templateContent
                    : templateContent;
                return { ...prev, decision: newDecision };
            });
        }
    };

    const openTemplateDialog = (type) => {
        setTemplateType(type);
        setTemplateDialogOpen(true);
    };

    const handleFileButtonClick = () => {
        if (fileDialogStatus) return;
        setFileDialogStatus(true);
        const fetchFiles = async () => {
            const res = await getObjectListByPropertyApi(DECISION_FILE, "decisionId", currentAgenda.id);
            const files = res.data || [];
            files.forEach(file => {
                file.user = userList.find(u => u.id === file.adminUserId);
            });
            setAgendaFileList(files);
        };
        fetchFiles();
    };

    const handleSave = async () => {

        const requiredFields = ["subjectId", "decisionNo", "agenda", "department"];
        let errors = {};
        requiredFields.forEach(field => {
            if (!agenda[field] || (typeof agenda[field] === "string" && agenda[field].trim() === "")) {
                errors[field] = "Bu alan zorunludur";
            }
        });
        if (Object.keys(errors).length > 0) {
            setAgendaError(errors);
            return;
        }

        if (type === 'update') {
            const { allowedMin, allowedMax } = getDecisionNoRange(agenda, currentAgendaList);
            const decisionNoValue = Number(agenda.decisionNo);
            if (decisionNoValue < allowedMin || decisionNoValue > allowedMax) {
                Swal.fire({
                    position: 'center',
                    icon: 'warning',
                    title: `Bu konu için ${decisionNoValue} karar numarası girilemez. Lütfen ${allowedMin} ile ${allowedMax} arasında bir sayı giriniz.`,
                    showConfirmButton: false,
                    timer: 2500,
                    customClass: {
                        container: 'swal-container-custom'
                    },
                });
                return;
            }
        }

        Swal.fire({
            title: 'İşleminiz gerçekleştiriliyor...',
            html: '<div><CircularProgress /></div>',
            allowOutsideClick: false,
            showConfirmButton: false,
            customClass: {
                container: 'swal-container-custom'
            },
            didOpen: () => Swal.showLoading()
        });
        if (type === "update") {

            const updatedAgenda = {
                ...agenda,
                pdate: new Date(),
                adminUserId: authUser.id
            };
            let agendaWithoutUser = { ...updatedAgenda };
            delete agendaWithoutUser.user;

            const result = await putUpdateObjectApi(DECISION, agendaWithoutUser);

            if (result.status) {

                const oldSupp = currentAgenda?.supp;
                const newSupp = updatedAgenda.supp;
                const becameExtra = oldSupp !== 'Evet' && newSupp === 'Evet';

                if (becameExtra) {
                    const maxExtraDecisionNo = Math.max(...currentAgendaList.filter(a => a.supp === 'Evet').map(a => a.decisionNo), 0);
                    updatedAgenda.decisionNo = maxExtraDecisionNo + 1;
                }

                const updateType = becameExtra ? "update" : "updateV2";

                dispatch(await updateSortedAgendaList(
                    currentAgendaList,
                    updateType,
                    { ...updatedAgenda },
                    true
                ));

                dispatch(showAgendaSnackbar({
                    message: `${updatedAgenda.decisionNo} numaralı gündemde değişiklik yapıldı.`,
                    severity: 'info'
                }));

            }
        } else {

            let newAgenda = { ...agenda };
            if (!newAgenda.decision || (typeof newAgenda.decision === "string" && newAgenda.decision.trim() === "")) {
                newAgenda.decision = newAgenda.agenda;
            }
            const result = await postSaveObjectApi(DECISION, newAgenda);

            if (result.status) {

                const newAgendaWithUser = { id: result.data.insertId, ...newAgenda, user: authUser }

                await dispatch(await updateSortedAgendaList(currentAgendaList, "change", newAgendaWithUser));
                dispatch(setCurrentAgenda(newAgendaWithUser));
                setAgenda(newAgendaWithUser);

                allMeetingList = allMeetingList.map(meeting => {
                    if (meeting.id === currentMeeting.id) {
                        return {
                            ...meeting,
                            decisionCount: currentAgendaList.length + 1,
                            user: authUser,
                            adminUserId: authUser.id
                        };
                    }
                    return meeting;
                });

                allMMList = allMMList.map(meeting => {
                    if (meeting.id === currentMeeting.id) {
                        return {
                            ...meeting,
                            decisionCount: currentAgendaList.length + 1,
                            user: authUser,
                            adminUserId: authUser.id
                        };
                    }
                    return meeting;
                });

                Swal.close();

                dispatch(setAllMeetingList(allMeetingList));
                dispatch(setAllMMList(allMMList));

                dispatch(showAgendaSnackbar({
                    message: `${newAgenda.decisionNo} numaralı gündem eklendi.`,
                    severity: 'success'
                }));

                Swal.fire({
                    position: 'center',
                    icon: 'success',
                    title: 'Ekleme başarılı.',
                    showConfirmButton: false,
                    timer: 2000,
                    customClass: {
                        container: 'swal-container-custom'
                    },
                });
            }
        }
        if (type === "update") {
            onClose();
        }
    };


    const handleChange = name => (e) => {

        const value = e.target.value;
        let updated = { ...agenda, [name]: value };

        if (name === 'supp') {
            if (value === 'Evet') {
                const extras = currentAgendaList.filter(a =>
                    a.supp === 'Evet' && a.id !== agenda.id
                );
                if (extras.length > 0) {
                    updated.decisionNo = Math.max(...extras.map(a => a.decisionNo), 0) + 1;
                } else {
                    updated.decisionNo = currentAgendaList.length + 1;
                }
            } else {
                // "Evet→Hayır" dönerken eski konu/dept grubuna göre hesapla
                const filt = currentAgendaList.filter(a =>
                    a.subjectId === agenda.subjectId &&
                    a.department === agenda.department &&
                    a.supp !== 'Evet'
                );
                updated.decisionNo = calculateNewDecisionNo(
                    currentAgendaList,
                    filt,
                    agenda.department
                );
            }
        }
        setAgenda(updated);
        setAgendaError({
            ...agendaError,
            [name]: ""
        });
    };


    const handleSubjectChange = (newSubjectId) => {
        const currentDept = agenda.department;
        let newDecisionNo;
        if (agenda.supp === 'Evet') {

            const extras = currentAgendaList.filter(a =>
                a.supp === 'Evet' && a.id !== agenda.id
            );
            if (extras.length > 0) {
                newDecisionNo = Math.max(...extras.map(a => a.decisionNo), 0) + 1;
            } else {
                newDecisionNo = currentAgendaList.length + 1;
            }
            setIsDecisionNoDisabled(false);
        } else {
            const filtered = currentAgendaList.filter(a =>
                a.subjectId === newSubjectId &&
                a.department === currentDept &&
                a.supp !== 'Evet'
            );
            newDecisionNo = calculateNewDecisionNo(currentAgendaList, filtered, currentDept);
            setIsDecisionNoDisabled(filtered.length === 0);
        }

        const updatedAgenda = {
            ...agenda,
            subjectId: newSubjectId,
            decisionNo: newDecisionNo
        };
        setAgenda(updatedAgenda);
        setAgendaError(prev => ({ ...prev, subjectId: "" }));
    };



    const handleDepartmentChange = (newDepartment) => {
        const newDept = newDepartment.map(item => item.name).join(',');
        let newDecisionNo;

        if (agenda.supp === 'Evet') {
            const extras = currentAgendaList.filter(a =>
                a.supp === 'Evet' && a.id !== agenda.id
            );
            newDecisionNo = extras.length + 1;
            setIsDecisionNoDisabled(false);
        } else {
            const filtered = currentAgendaList.filter(a =>
                a.subjectId === agenda.subjectId &&
                a.department === newDept &&
                a.supp !== 'Evet'
            );
            newDecisionNo = calculateNewDecisionNo(currentAgendaList, filtered, newDept);
            setIsDecisionNoDisabled(filtered.length === 0);
        }

        const updatedAgenda = {
            ...agenda,
            department: newDept,
            decisionNo: newDecisionNo
        };
        setAgenda(updatedAgenda);
        setAgendaError(prev => ({ ...prev, department: "" }));
    };

    const disabled = type === "preview";
    return (
        <>
            <JumboCardQuick>
                <Grid container spacing={1} rowSpacing={1}>

                    <Grid item xs={3} md={3} lg={3}>
                        <FormControl fullWidth>
                            <Autocomplete
                                required
                                onChange={(event, newValue) => {
                                    handleSubjectChange(newValue ? newValue.id : "");
                                }}
                                name="subjectId"
                                label="Konu"
                                placeholder="Konu"
                                value={subjectList.find(option => option.id === agenda.subjectId) || null}
                                getOptionLabel={(option) => option.name}
                                options={subjectList}
                                renderInput={(params2) => (
                                    <TextField
                                        {...params2}
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                        required
                                        placeholder="Konu"
                                        label="Konu"
                                        error={Boolean(agendaError.subjectId)}
                                        helperText={agendaError.subjectId}
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={3} md={3} lg={2}>
                        <TextField
                            required
                            variant="outlined"
                            placeholder="Gündem No"
                            fullWidth
                            value={agenda.decisionNo}
                            type="number"
                            onChange={handleChange("decisionNo")}
                            InputLabelProps={{
                                shrink: true
                            }}
                            label="Gündem No"
                            disabled={true}
                            // disabled={isDecisionNoDisabled || disabled || agenda.supp === "Evet"}
                            error={Boolean(agendaError.decisionNo)}
                            helperText={agendaError.decisionNo}
                        />
                    </Grid>


                    {/* Bölüm (department) alanı - Autocomplete ile çoklu seçim */}
                    <Grid item xs={3} md={3} lg={3}>
                        <FormControl fullWidth>

                            <Autocomplete
                                multiple
                                required
                                options={departmentList}
                                getOptionLabel={(option) => option.name}
                                onChange={(event, newValue) => {
                                    handleDepartmentChange(newValue);
                                }}
                                value={
                                    agenda.department && agenda.department.trim() !== ""
                                        ? departmentList.filter(item =>
                                            agenda.department.split(',').map(s => s.trim()).includes(item.name)
                                        )
                                        : currentMeeting && currentMeeting.inst
                                            ? departmentList.filter(item => item.name === currentMeeting.inst)
                                            : []
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Bölüm"
                                        placeholder="Bölüm seçiniz"
                                        error={Boolean(agendaError.department)}
                                        helperText={agendaError.department}
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                    />
                                )}
                            />


                        </FormControl>
                    </Grid>

                    <Grid item xs={3} md={2} lg={2}>
                        <FormControl>
                            <FormLabel id="principle-label">İlke Kararı</FormLabel>
                            <RadioGroup
                                aria-labelledby="principle-label"
                                name="radio-buttons-group"
                                onChange={handleChange("principle")}
                                value={agenda.principle}
                                row
                                disabled={disabled}
                            >
                                <FormControlLabel value={"Hayır"} disabled={disabled} control={<Radio />} label="Hayır" />
                                <FormControlLabel value={"Evet"} disabled={disabled} control={<Radio />} label="Evet" />
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    <Grid item xs={3} md={2} lg={2}>
                        <FormControl>
                            <FormLabel id="supp-label">Ek Gündem</FormLabel>
                            <RadioGroup
                                aria-labelledby="supp-label"
                                name="radio-buttons-group"
                                onChange={handleChange("supp")}
                                value={agenda.supp}
                                row
                                disabled={disabled}
                            >
                                <FormControlLabel value={"Hayır"} disabled={disabled} control={<Radio />} label="Hayır" />
                                <FormControlLabel value={"Evet"} disabled={disabled} control={<Radio />} label="Evet" />
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    {
                        currentMeeting.inst === "YK" ?
                            <>
                                <Grid item xs={6} md={6} lg={6}>
                                    <TextField
                                        variant="outlined"
                                        label="Yaysis Kayıt Numaraları"
                                        placeholder="Yaysis Kayıt Numaraları"
                                        multiline
                                        fullWidth
                                        value={agenda.yaysisPubIdList}
                                        onChange={handleChange("yaysisPubIdList")}
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={3} md={3} lg={3}>
                                    <FormControl>
                                        <FormLabel id="secret-label">Gizli Karar</FormLabel>
                                        <RadioGroup
                                            aria-labelledby="secret-label"
                                            name="radio-buttons-group"
                                            onChange={handleChange("secret")}
                                            value={agenda.secret}
                                            row
                                            disabled={disabled}
                                        >
                                            <FormControlLabel value={"Hayır"} disabled={disabled} control={<Radio />} label="Hayır" />
                                            <FormControlLabel value={"Evet"} disabled={disabled} control={<Radio />} label="Evet" />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                            </> : ""
                    }

                    <Grid item xs={12} md={12} lg={12}>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() => openTemplateDialog('agenda')}
                            >
                                Gündemi Şablondan Getir
                            </Button>
                        </Box>
                        <TextField
                            required
                            variant="outlined"
                            placeholder="Gündem"
                            multiline
                            fullWidth
                            value={stripHTMLTags(agenda.agenda)}
                            type="number"
                            rows={8}
                            onChange={handleChange("agenda")}
                            InputLabelProps={{
                                shrink: true
                            }}
                            label="Gündem"
                            error={Boolean(agendaError.agenda)}
                            helperText={agendaError.agenda}
                        />
                    </Grid>

                    <Grid item xs={12} md={12} lg={12}>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() => openTemplateDialog('decision')}
                            >
                                Kararı Şablondan Getir
                            </Button>
                        </Box>
                        <TextField
                            variant="outlined"
                            placeholder="Karar (Gündemden farklı ise giriniz. Karar gündem ile aynı ise, gündem karara otomatik kopyalanacaktır.)"
                            multiline
                            fullWidth

                            value={stripHTMLTags(agenda.decision)}
                            onChange={handleChange("decision")}
                            InputLabelProps={{
                                shrink: true
                            }}
                            label="Karar"
                            rows={8}
                            inputProps={{
                                style: { overflow: 'hidden' } // Prevent scroll
                            }}
                        />
                    </Grid>

                </Grid>
            </JumboCardQuick>
            <br />

            <Card>
                <Box
                    sx={{
                        py: 1,
                        px: 1,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 0.5,
                    }}
                >
                    {isUpdateMode && (
                        <Button variant="contained" onClick={handleFileButtonClick}>
                            Dosyalar
                        </Button>
                    )}
                    <Button variant="contained" color="success" onClick={() => handleSave()}>
                        {isUpdateMode ? 'Güncelle' : 'Kaydet'}
                    </Button>
                    <Button variant="contained" color="error" onClick={() => onClose()}>
                        Kapat
                    </Button>
                </Box>
            </Card>

            <AgendaTemplateSelectionDialog
                open={templateDialogOpen}
                onClose={() => setTemplateDialogOpen(false)}
                onTemplateSelect={handleTemplateSelect}
                templateType={templateType}
            />

            {fileDialogStatus && (
                <AgendaFileDialog
                    agendaFileList={agendaFileList}
                    setAgendaFileList={setAgendaFileList}
                    onClose={() => setFileDialogStatus(false)}
                    open={fileDialogStatus}
                />
            )}
        </>
    );
}

const style = document.createElement('style');
style.innerHTML = `
    .swal-container-custom {
        z-index: 10000 !important;
    }
`;
document.head.appendChild(style);

export default AddUpdateAgendaCard;
